/**
 * FCRList Component
 *
 * Displays a table of Forwarded Customer Requests (FCRs)
 * for the Dashboard when the FCR filter is selected.
 */

import { ForwardedRequest, MatchConfidence } from '../services/types';

interface FCRRowProps {
  fcr: ForwardedRequest;
  onClick: () => void;
}

function formatAge(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'yesterday';
  return `${diffDays}d ago`;
}

function getConfidenceBadge(confidence: MatchConfidence): { label: string; classes: string } {
  switch (confidence) {
    case 'high':
      return { label: 'High', classes: 'bg-green-500/20 text-green-400' };
    case 'medium':
      return { label: 'Medium', classes: 'bg-yellow-500/20 text-yellow-400' };
    case 'low':
      return { label: 'Low', classes: 'bg-orange-500/20 text-orange-400' };
    case 'none':
      return { label: 'No match', classes: 'bg-red-500/20 text-red-400' };
  }
}

function FCRRow({ fcr, onClick }: FCRRowProps) {
  const confidenceBadge = getConfidenceBadge(fcr.matchConfidence);
  const forwarderCount = 1 + (fcr.additionalForwarders?.length || 0);

  return (
    <tr
      className="border-b border-slate-700 hover:bg-slate-700/50 cursor-pointer"
      onClick={onClick}
    >
      {/* Customer Email */}
      <td className="py-2 px-3">
        <div className="flex flex-col">
          <span className="text-white text-sm">
            {fcr.originalFrom.name || fcr.originalFrom.email}
          </span>
          {fcr.originalFrom.name && (
            <span className="text-slate-400 text-xs">{fcr.originalFrom.email}</span>
          )}
        </div>
      </td>

      {/* Subject */}
      <td className="py-2 px-3">
        <div className="text-slate-300 text-sm truncate max-w-xs" title={fcr.originalSubject}>
          {fcr.originalSubject || '(no subject)'}
        </div>
      </td>

      {/* Company Match */}
      <td className="py-2 px-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-white text-sm">
            {fcr.matchedCompanyName || 'Unknown'}
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded w-fit ${confidenceBadge.classes}`}>
            {confidenceBadge.label}
          </span>
        </div>
      </td>

      {/* Forwarded By */}
      <td className="py-2 px-3">
        <div className="flex items-center gap-1">
          <span className="text-slate-300 text-sm truncate max-w-[120px]" title={fcr.forwardedBy}>
            {fcr.forwardedBy.split('@')[0]}
          </span>
          {forwarderCount > 1 && (
            <span
              className="text-[10px] bg-slate-600 text-slate-300 px-1 py-0.5 rounded"
              title={`${forwarderCount} techs forwarded this`}
            >
              +{forwarderCount - 1}
            </span>
          )}
        </div>
      </td>

      {/* Status */}
      <td className="py-2 px-3">
        <span className={`text-xs px-2 py-0.5 rounded ${
          fcr.status === 'pending' ? 'bg-blue-500/20 text-blue-400' :
          fcr.status === 'processing' ? 'bg-yellow-500/20 text-yellow-400' :
          fcr.status === 'completed' ? 'bg-green-500/20 text-green-400' :
          fcr.status === 'ignored' ? 'bg-slate-500/20 text-slate-400' :
          'bg-red-500/20 text-red-400'
        }`}>
          {fcr.status === 'pending' ? 'Pending' :
           fcr.status === 'processing' ? 'Processing' :
           fcr.status === 'completed' ? 'Completed' :
           fcr.status === 'ignored' ? 'Ignored' :
           'Error'}
        </span>
      </td>

      {/* Age */}
      <td className="py-2 px-3 text-slate-400 text-xs font-mono">
        {formatAge(fcr.receivedAt)}
      </td>
    </tr>
  );
}

interface FCRListProps {
  fcrs: ForwardedRequest[];
  onFCRClick: (fcr: ForwardedRequest) => void;
  loading?: boolean;
  emptyMessage?: string;
}

export default function FCRList({ fcrs, onFCRClick, loading, emptyMessage }: FCRListProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  if (fcrs.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <p>{emptyMessage || 'No forwarded requests to process'}</p>
        {!emptyMessage && (
          <p className="text-sm mt-1">Forward customer emails to fcr@umetech.net</p>
        )}
      </div>
    );
  }

  return (
    <table className="w-full">
      <thead>
        <tr className="text-left text-xs text-slate-400">
          <th className="pb-2 px-3 font-medium">Customer</th>
          <th className="pb-2 px-3 font-medium">Subject</th>
          <th className="pb-2 px-3 font-medium">Company</th>
          <th className="pb-2 px-3 font-medium">Forwarded By</th>
          <th className="pb-2 px-3 font-medium">Status</th>
          <th className="pb-2 px-3 font-medium">Age</th>
        </tr>
      </thead>
      <tbody>
        {fcrs.map(fcr => (
          <FCRRow
            key={fcr.id}
            fcr={fcr}
            onClick={() => onFCRClick(fcr)}
          />
        ))}
      </tbody>
    </table>
  );
}
